import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';

import { EmojiPicker } from '../../../../../../_shared';
import { messages } from '../../../../../../_shared/i18n/messages';

import { insertEmoji } from '../commands';

const propTypes = {

	/**
	 * The id of the DOM element where the editor will be injected.
	 */
	editorDomId: PropTypes.string.isRequired,

	/*
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

	/**
	 * Flag indicating if button behavior should be configured for inline placement.
	 */
	inline: PropTypes.bool,

	/**
	 * The provided toolbar props to adhere to accessibility (a11y)
	 * guidelines for button toolbars.
	 *
	 * See here: https://www.w3.org/WAI/ARIA/apg/patterns/toolbar
	 */
	a11yToolbarProps: PropTypes.shape({

		/**
		 * The tab index value for the button. Only the button with
		 * current focus should have tab index value of 0. All others
		 * should have value of -1.
		 */
		tabIndex: PropTypes.oneOf(['0', '-1']).isRequired,

		/**
		 * Callback function to handle keypress events on the button.
		 */
		onKeyDown: PropTypes.func.isRequired,

	}),

};

const EmojiButton = (props) => {

	const {
		editorDomId,
		editorView,
		inline,
		a11yToolbarProps = {},
	} = props;

	const intl = useIntl();

	const handleSelect = useCallback((data) => {

		const { state, dispatch } = editorView;

		insertEmoji(data)(state, dispatch);

		editorView.focus();

	}, [editorView]);

	return (
		<EmojiPicker
			triggerButtonId={`${editorDomId}-toolbar-btn-emoji`}
			triggerButtonClassName={`tc-text-editor-toolbar-btn ${inline ? 'inline' : ''}`}
			triggerTitle={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_EMOJI_BUTTON_TITLE)}
			triggerButtonSize={inline ? '30px' : '25px'}
			popoverPlacement={inline ? 'top-end' : 'bottom-start'}
			onSelect={handleSelect}
			{...a11yToolbarProps} />
	);

};

EmojiButton.propTypes = propTypes;

export default EmojiButton;
